import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { fromJS } from 'immutable';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { WindowRefService } from 'i2g-ng-utils';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import {
  IAddManualPayment,
  IInvalidEmailConfirmDialogData,
  ISendReceiptPreview,
} from '../../../payments-data/payments-data.data';
import {
  CompanySettings,
  ManualPaymentReceiptsEnabledEnum,
} from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { IAutoCompleteOptions } from '../../../shared/directives/auto-complete/auto-complete.interface';
import { EmailService } from '../../../shared/services/email/email.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { ManualPaymentDialogBuilder } from './manual-payment-dialog.builder';
import { ToasterDispatcher } from '../../../toaster/toaster.dispatcher';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { DocumentTypesEnum } from '../../../document-data/document-data.data';
import { get } from 'lodash';
import { environment } from '../../../../../environments/environment';

export const DIALOG_TYPE = 'manual-payment';

export interface IManualPaymentDialogData {
  asFullyPaid?: boolean;
  currencyCode?: string;
  locale?: string;
  calculationResult?: any;
  paymentUrl?: string;
  documentBilling?: Billing;
  document?: Document;
}

@Component({
  selector: 'app-manual-payment-dialog',
  templateUrl: './manual-payment-dialog.component.html',
  styleUrls: ['./manual-payment-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ManualPaymentDialogBuilder],
})
export class ManualPaymentDialogComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() currencyCode: string;
  @Input() locale: string;
  @Input() calculationResult: CalculationResult;
  @Input() paymentUrl: string;
  @Input() document: Document;
  @Input() documentBilling: Billing;
  @Input() companySettings: CompanySettings;
  @Input() invalidEmailDialogId: string;
  @Input() isKicEnablingPayment = false;

  @Output()
  onAddManualPayment: EventEmitter<IAddManualPayment> = new EventEmitter<IAddManualPayment>();
  @Output() onKICPaymentSuccess: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onSendPreviewReceipt: EventEmitter<ISendReceiptPreview> = new EventEmitter<ISendReceiptPreview>();
  @Output() onDialogClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onEnableCardPayments: EventEmitter<Document> = new EventEmitter<Document>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _window: any;
  private _bindListener: any;
  private _validEmail: boolean;
  private _skipFirstOnLoad = true;
  private _preventUrlChangesAfterPaid = false;
  private _isKIC$: BehaviorSubject<boolean>;

  form: FormGroup;
  formKeys: any;
  methodOptions: { autoComplete: IAutoCompleteOptions };
  terminalUrl: SafeUrl;
  dialogType: string;
  paymentReceiptsEnabled: boolean;
  featureHighlightIcon: IIcon;
  addPaymentActionAdditionalProperties: any;
  paymentReceiptsOn: boolean;
  loadingSpinnerIcon: IIcon;
  trackingObject: ITrackingObject;
  addPaymentAction: ITrackingAction;
  cancelAction: ITrackingAction;
  sendPreviewAction: ITrackingAction;
  showKICButton$: Observable<boolean>;
  isKIC$: Observable<boolean>;
  showLargeKicDialog$: BehaviorSubject<boolean>;

  constructor(
    private _manualPaymentBuilder: ManualPaymentDialogBuilder,
    private _dialogService: DialogService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingDispatcher: TrackingDispatcher,
    private _windowRefService: WindowRefService,
    private _featuresDataStore: FeaturesDataStore,
    private _toasterDispatcher: ToasterDispatcher,
    private _emailService: EmailService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.companySettings && this.companySettings) {
      this.paymentReceiptsOn =
        this.companySettings.manualPaymentReceiptsEnabled ===
        ManualPaymentReceiptsEnabledEnum.on;
    }

    if (changes.paymentUrl && !this._preventUrlChangesAfterPaid) {
      this.terminalUrl = this._manualPaymentBuilder.buildTerminalUrl(
        this.paymentUrl,
        this.isKicEnablingPayment
      );
    }

    if (
      changes.isKicEnablingPayment &&
      changes.isKicEnablingPayment.previousValue === true &&
      changes.isKicEnablingPayment.currentValue === false
    ) {
      this.terminalUrl = this._manualPaymentBuilder.buildTerminalUrl(
        this.paymentUrl,
        true
      );
    }
  }

  ngOnInit() {
    const paymentReceiptsFeatureName = Toggles.KEYS.PAYMENT_RECEIPTS;

    this._window = this._windowRefService.nativeWindow;
    this._bindListener = this.portalMessageListener.bind(this);
    this.dialogType = DIALOG_TYPE;
    this.formKeys = this._manualPaymentBuilder.FORM_KEYS;
    this.methodOptions = this._manualPaymentBuilder.buildMethodOptions();
    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.invoice_record_transaction
    );
    this.addPaymentAction = this._trackingActionBuilder.buildButtonTapped(
      'add_transaction',
      this.trackingScreenName
    );
    this.cancelAction = this._trackingActionBuilder.buildButtonTapped(
      'cancel',
      this.trackingScreenName
    );
    this.sendPreviewAction = this._trackingActionBuilder.buildButtonTapped(
      'send_preview',
      this.trackingScreenName
    );

    this._isKIC$ = new BehaviorSubject<boolean>(false);
    this.showLargeKicDialog$ = new BehaviorSubject<boolean>(false);

    this.isKIC$ = this._isKIC$.asObservable().pipe(shareReplay(1));

    this._featuresDataStore
      .hasFeatureHighlightCanvas(paymentReceiptsFeatureName)
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x)
      )
      .subscribe(() => {
        this.featureHighlightIcon = {
          name: 'premium-feature',
          size: IconSizesEnum.medium,
          customClass: 'premium-icon',
        };
      });

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ type, action }: IDialogTrigger) =>
            type === this.dialogType && action === DialogActionsEnum.refresh
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this._changeDetectorRef.markForCheck());

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ type, action }: IDialogTrigger) =>
            type === this.dialogType && action === DialogActionsEnum.open
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(({ data }: IDialogTrigger) => {
        const {
          document,
          documentBilling,
          currencyCode,
          locale,
          calculationResult,
          paymentUrl,
          asFullyPaid,
        } = data;

        if (document) {
          this.document = document;
        }

        if (documentBilling) {
          this.documentBilling = documentBilling;
        }

        this._validEmail =
          this.documentBilling &&
          this._emailService.validateEmails(this.documentBilling.email);

        if (currencyCode) {
          this.currencyCode = currencyCode;
        }

        if (locale) {
          this.locale = locale;
        }

        if (calculationResult) {
          this.calculationResult = calculationResult;
        }

        if (paymentUrl) {
          this.paymentUrl = paymentUrl;
        }

        this._preventUrlChangesAfterPaid = false;
        this._isKIC$.next(false);
        this.showLargeKicDialog$.next(false);
        this.terminalUrl = this._manualPaymentBuilder.buildTerminalUrl(
          this.paymentUrl,
          this.isKicEnablingPayment
        );
        this.loadingSpinnerIcon = {
          name: 'loading-spinner',
          size: IconSizesEnum.large,
        };

        const paymentReceiptsAllAccess$ = this._featuresDataStore.isAllAccess(
          paymentReceiptsFeatureName
        );

        paymentReceiptsAllAccess$.subscribe(
          (paymentReceiptsAllAccess: boolean) => {
            this.paymentReceiptsEnabled =
              this.paymentReceiptsOn &&
              paymentReceiptsAllAccess &&
              this._validEmail;
            this.form = this._manualPaymentBuilder.buildForm(
              this.calculationResult,
              asFullyPaid,
              this.paymentReceiptsEnabled
            );
            this.addPaymentActionAdditionalProperties = {
              send_receipt: this.paymentReceiptsEnabled,
            };
          }
        );

        this.showKICButton$ = this._featuresDataStore
          .isAllAccess(Toggles.KEYS.CHARGE_CARD_IN_APP)
          .pipe(
            map(
              (isAllAccess: boolean) =>
                this.calculationResult.outstandingBalance > 0 &&
                !this.companySettings.payments.cardPayments.stripe.isIneligible() &&
                isAllAccess
            )
          );

        this.form
          .get(this.formKeys.FULLY_PAID)
          .valueChanges.pipe(
            filter((x) => !!x),
            takeUntil(this._destroyed)
          )
          .subscribe(() => {
            this.form
              .get(this.formKeys.AMOUNT)
              .setValue(this.calculationResult.outstandingBalance, {
                emitEvent: false,
              });
          });

        this.form
          .get(this.formKeys.AMOUNT)
          .valueChanges.pipe(
            filter(
              (amount) => amount < this.calculationResult.outstandingBalance
            ),
            takeUntil(this._destroyed)
          )
          .subscribe(() =>
            this.form
              .get(this.formKeys.FULLY_PAID)
              .setValue(false, { emitEvent: false })
          );

        const sendReceiptToggleControl = this.form.get(
          this.formKeys.SEND_RECEIPT
        );

        combineLatest(
          sendReceiptToggleControl.valueChanges,
          paymentReceiptsAllAccess$
        )
          .pipe(takeUntil(this._destroyed))
          .subscribe(([value, allAccess]) => {
            if (!allAccess || !this._validEmail) {
              sendReceiptToggleControl.setValue(!value, { emitEvent: false });

              if (!allAccess) {
                this.showFeatureLockDialog();
              } else {
                const email = get(this.documentBilling, Billing.KEYS.EMAIL, '');

                if (!email || !this._emailService.validateEmails(email)) {
                  this._dialogService.openConfirmDialog(
                    this.invalidEmailDialogId,
                    {
                      documentType: this.document.documentType,
                      documentId: this.document.id,
                    } as IInvalidEmailConfirmDialogData
                  );
                }
              }
            } else {
              this.addPaymentActionAdditionalProperties = {
                send_receipt: value,
              };

              this._changeDetectorRef.markForCheck();
              this._changeDetectorRef.detectChanges();
            }
          });

        this._window.addEventListener('message', this._bindListener, true);

        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
      });
  }

  onAddPayment() {
    this._featuresDataStore
      .getFeatureToggles()
      .pipe(takeUntil(this._destroyed))
      .subscribe((featureToggles: Toggles) => {
        const renderingOptions = this._manualPaymentBuilder.buildRenderingOptions(
          this.companySettings.payments,
          this.document,
          featureToggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT),
          featureToggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS),
          featureToggles.isAllAccess(Toggles.KEYS.PAYPAL_EC)
        );
        const payload = this._manualPaymentBuilder.buildAddManualPayment(
          fromJS(this.form.value),
          renderingOptions,
          this.document
        );

        this.onAddManualPayment.emit(payload);
      });
  }

  onPreviewReceipt() {
    this._featuresDataStore
      .getFeatureToggles()
      .pipe(takeUntil(this._destroyed))
      .subscribe((featureToggles: Toggles) => {
        const renderingOptions = this._manualPaymentBuilder.buildRenderingOptions(
          this.companySettings.payments,
          this.document,
          featureToggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT),
          featureToggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS),
          featureToggles.isAllAccess(Toggles.KEYS.PAYPAL_EC)
        );
        const payload = this._manualPaymentBuilder.buildSendReceiptPreview(
          fromJS(this.form.value),
          this.dialogType,
          renderingOptions,
          this.document
        );

        this.onSendPreviewReceipt.emit(payload);
      });
  }

  dialogClosed() {
    this._window.removeEventListener('message', this._bindListener, true);
    this._toasterDispatcher.clearToast(null);
    this.onDialogClose.emit();
  }

  chargeCard() {
    // set timeout so enable card payments starts before we load the iframe
    setTimeout(() => this._isKIC$.next(true));

    if (
      !this.companySettings.payments.cardPaymentsEnabled() ||
      this.document.settings.cardPaymentsDisabled
    ) {
      this.showLargeKicDialog$.next(true);

      this.onEnableCardPayments.emit(this.document);
    }
  }

  onKICLoad() {
    // skip first onload event from iframe since it's triggered twice by angular
    if (this._skipFirstOnLoad) {
      this._skipFirstOnLoad = false;
      return;
    }

    this._skipFirstOnLoad = true;

    this._trackingDispatcher.trackPage(
      this.document.documentType === DocumentTypesEnum.invoice
        ? TrackingScreenNamesEnum.invoice_charge_a_card
        : null
    );
  }

  portalMessageListener(evt: any) {
    // Only listen to messages from portal
    if (
      evt.origin === environment.paymentsPortalUrl &&
      evt.data === 'payment-success'
    ) {
      this._preventUrlChangesAfterPaid = true;

      setTimeout(() => this.onKICPaymentSuccess.emit(), 3000);
    }
  }

  showFeatureLockDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      Toggles.KEYS.PAYMENT_RECEIPTS,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      Toggles.KEYS.PAYMENT_RECEIPTS,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  ngOnDestroy() {
    const paymentReceiptsFeatureName = Toggles.KEYS.PAYMENT_RECEIPTS;

    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      paymentReceiptsFeatureName
    );
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      paymentReceiptsFeatureName
    );
    this._dialogService.closeConfirmDialog(this.invalidEmailDialogId);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
